/**
 * Make-good credits on the screen before a start (Warren, 2026-10-28): the
 * credit is a wallet credit that behaves like a next-session credit, so while
 * one is unspent and the wallet can pay THIS session under the gate's own
 * rules (the preview's wallet option), the Card/Wallet choice opens on Wallet
 * with one line saying the credit will be used. The driver can still switch
 * to Card.
 *
 *   - startCredit: the credit this start would use: in the currency of the
 *     wallet that would pay (the option's own: a CAD wallet at a CAD charger,
 *     a USD wallet at a US one, or a USD wallet converted at a CAD charger),
 *     with something left, not expired, on a session that bills (a free
 *     session takes nothing from the wallet, so no credit is used).
 *   - startPaymentSource: the gate reads the driver's stored choice
 *     (User.preferred_payment_source), not the screen. When the stored one
 *     would resolve differently from the checked option, Start sends the
 *     checked one with the start (driver-start's payment_source), for that
 *     start only: Wallet checked (the credit default) over a stored Card, or
 *     over no stored choice while a card can pay (the gate's default is then
 *     the card); Card checked over a stored Wallet. Nothing is saved, so the
 *     credit's default never changes what the driver's RFID card pays with:
 *     a stored Card stays Card after the credit has drawn the wallet below
 *     its minimum. Otherwise nothing is sent and the stored choice pays.
 *
 * Pure, so node tests drive it directly.
 */
import type { WalletCredit } from '@/lib/api';
import type { WalletOptionView } from './walletOptionView';
import type { PaymentSource } from './paymentChoice';

export function startCredit(
  view: Pick<WalletOptionView, 'selectable' | 'currency' | 'requiredCents'> | null,
  credits: readonly WalletCredit[] | undefined,
  now: Date = new Date(),
): WalletCredit | null {
  if (!view?.selectable || view.requiredCents <= 0) return null;
  const currency = view.currency.toUpperCase();
  return (credits ?? []).find((c) => c.currency.toUpperCase() === currency && c.remainingCents > 0
    && Number.isFinite(Date.parse(c.expiresAt)) && Date.parse(c.expiresAt) > now.getTime()) ?? null;
}

export function startPaymentSource(
  selected: PaymentSource | null,
  stored: PaymentSource | null | undefined,
  cardAvailable: boolean,
): PaymentSource | null {
  if (selected === 'wallet' && (stored === 'card' || (stored == null && cardAvailable))) return 'wallet';
  if (selected === 'card' && stored === 'wallet') return 'card';
  return null;
}
